import React from 'react';

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-[22px] sm:text-2xl md:text-3xl font-medium mb-3 sm:mb-4 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
      {title}
    </h2>
    {children}
  </div>
);

const TermsOfService = () => {
  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10 sm:py-16 text-[#1a1a1a]">

      <h1 className="text-[32px] sm:text-4xl md:text-5xl font-medium mb-5 sm:mb-8 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
        Terms of Service
      </h1>

      <p className="text-sm mb-7 sm:mb-10 opacity-70">Last updated: October 6, 2025</p>

      <div className="prose prose-lg max-w-none text-[15px] sm:text-[16px] leading-relaxed space-y-7 sm:space-y-8">

        <p>
          Welcome to Aishcraft. By accessing or using our website and purchasing our products, you
          agree to be bound by the following Terms of Service. Please read them carefully before
          placing an order or using any part of our website. If you do not agree to these terms,
          please do not use our Services.
        </p>

        <p>
          These Terms of Service apply to all users of the site, including browsers, customers,
          merchants, and contributors of content. Aishcraft reserves the right to update or modify
          these terms at any time without prior notice. Your continued use of the website following
          any changes constitutes your acceptance of the revised terms.
        </p>

        {/* 1 */}
        <Section title="Acceptance of Terms">
          <p>
            By visiting our website, creating an account, or placing an order, you confirm that you
            are at least 18 years of age, or that you have the consent of a legal guardian, and that
            you agree to these Terms of Service and our Privacy Policy.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>You must be 18 years or older to make a purchase on Aishcraft.</li>
            <li>By using the site, you represent that all information you provide is accurate and current.</li>
            <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
            <li>Aishcraft reserves the right to refuse service to anyone for any reason at any time.</li>
          </ul>
        </Section>

        {/* 2 */}
        <Section title="Products and Services">
          <p>
            Aishcraft offers a curated range of stationery, gifting, and personalised products
            through our online store. We reserve the right to modify, discontinue, or limit the
            availability of any product at any time without notice.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Product images are for illustrative purposes only; actual colours may vary slightly due to screen settings.</li>
            <li>We reserve the right to limit quantities of any product per order or per customer.</li>
            <li>Certain products may be available exclusively online and not in any physical store.</li>
            <li>We do not guarantee that the quality of any product, service, or information will meet your expectations.</li>
            <li>Personalised and custom-made products are made to order and cannot be cancelled once production begins.</li>
          </ul>
        </Section>

        {/* 3 */}
        <Section title="Pricing and Payment">
          <p>
            All prices displayed on Aishcraft are in Indian Rupees (INR) and are inclusive of
            applicable taxes unless stated otherwise. We reserve the right to change prices at any
            time without prior notice.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Prices are subject to change without notice.</li>
            <li>We reserve the right to cancel any order where a pricing error has occurred.</li>
            <li>Payment must be made in full at the time of placing your order.</li>
            <li>We accept major credit/debit cards, UPI, net banking, and wallets via our secure payment partners.</li>
            <li>Aishcraft does not store your payment card information — all transactions are handled securely by our payment gateway.</li>
            <li>In the event of a failed transaction, please check with your bank before attempting to place the order again.</li>
          </ul>
        </Section>

        {/* 4 */}
        <Section title="Orders and Cancellations">
          <p>
            Once an order is placed, you will receive an email confirmation. This confirmation does
            not constitute acceptance of your order — Aishcraft reserves the right to cancel or
            refuse any order for reasons including product unavailability, errors in pricing, or
            suspected fraud.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Orders can be cancelled within 24 hours of placement by emailing hello@aishcraft.com.</li>
            <li>Custom or personalised orders cannot be cancelled once production has begun.</li>
            <li>If we cancel your order, a full refund will be issued to your original payment method.</li>
            <li>We are not responsible for delays caused by incorrect or incomplete delivery information provided at checkout.</li>
            <li>Aishcraft reserves the right to cancel bulk or resale orders without prior notice.</li>
          </ul>
        </Section>

        {/* 5 */}
        <Section title="Shipping and Delivery">
          <p>
            We aim to dispatch all orders within the timeframes stated on our website. Delivery
            timelines are estimates and may vary depending on location, courier availability, and
            other factors outside our control.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Aishcraft is not responsible for delays caused by courier services or customs clearance.</li>
            <li>Risk of loss and title for products passes to you upon delivery to the carrier.</li>
            <li>If an order is marked as delivered but not received, please contact us within 48 hours.</li>
            <li>Free shipping thresholds and delivery charges are subject to change at any time.</li>
            <li>We currently ship within India. International shipping availability may vary.</li>
          </ul>
        </Section>

        {/* 6 */}
        <Section title="Intellectual Property">
          <p>
            All content on the Aishcraft website, including but not limited to text, graphics, logos,
            product images, icons, and software, is the property of Aishcraft and is protected by
            applicable intellectual property laws.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>You may not reproduce, duplicate, copy, sell, or exploit any portion of the website without express written permission from Aishcraft.</li>
            <li>Our trademarks and trade dress may not be used in connection with any other product or service.</li>
            <li>User-submitted content (such as reviews or photos) grants Aishcraft a non-exclusive licence to use, display, and distribute that content.</li>
            <li>Any unauthorised use of our intellectual property may result in legal action.</li>
          </ul>
        </Section>

        {/* 7 */}
        <Section title="User Accounts">
          <p>
            When you create an account with Aishcraft, you are responsible for maintaining the
            security of your account and all activity that occurs under your account.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>You must provide accurate, complete, and current information when creating an account.</li>
            <li>You are responsible for all activity that occurs under your account credentials.</li>
            <li>You must notify us immediately at hello@aishcraft.com of any unauthorised use of your account.</li>
            <li>Aishcraft will not be liable for any loss or damage arising from your failure to maintain the security of your account.</li>
            <li>We reserve the right to terminate accounts that violate these terms or engage in fraudulent activity.</li>
          </ul>
        </Section>

        {/* 8 */}
        <Section title="Prohibited Activities">
          <p>
            You agree not to engage in any of the following activities while using the Aishcraft
            website or services:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Using our site for any unlawful purpose or in violation of any applicable laws or regulations.</li>
            <li>Attempting to gain unauthorised access to any part of the website or its related systems.</li>
            <li>Transmitting any viruses, malware, or other harmful code.</li>
            <li>Scraping, crawling, or using automated tools to collect data from our website without permission.</li>
            <li>Posting false, misleading, or defamatory reviews or content.</li>
            <li>Reselling products purchased on Aishcraft without prior written consent.</li>
            <li>Impersonating any person or entity, or misrepresenting your affiliation with any person or entity.</li>
          </ul>
        </Section>

        {/* 9 */}
        <Section title="Disclaimer of Warranties">
          <p>
            Aishcraft provides its services on an "as is" and "as available" basis without any
            warranties of any kind, either express or implied. We do not warrant that:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>The website will be uninterrupted, error-free, or free of viruses or other harmful components.</li>
            <li>The results obtained from using the website will be accurate or reliable.</li>
            <li>The quality of any products or services purchased will meet your expectations.</li>
            <li>Any errors in the website will be corrected in a timely manner.</li>
          </ul>
          <p className="mt-3">
            Your use of the service is at your sole risk. This does not affect your statutory rights
            as a consumer.
          </p>
        </Section>

        {/* 10 */}
        <Section title="Limitation of Liability">
          <p>
            To the fullest extent permitted by applicable law, Aishcraft shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages arising from your use
            of our website or products.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Our total liability to you for any claim shall not exceed the amount paid by you for the product in question.</li>
            <li>We are not liable for any loss of data, revenue, profits, or business arising from use of our services.</li>
            <li>We are not responsible for third-party websites linked to or from our site.</li>
            <li>This limitation of liability applies to the fullest extent permitted by law in your jurisdiction.</li>
          </ul>
        </Section>

        {/* 11 */}
        <Section title="Governing Law">
          <p>
            These Terms of Service shall be governed by and construed in accordance with the laws of
            India. Any disputes arising out of or in connection with these terms shall be subject to
            the exclusive jurisdiction of the courts located in Bhopal, Madhya Pradesh, India.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>These terms are governed by the laws of India.</li>
            <li>Any disputes will be resolved in the courts of Bhopal, Madhya Pradesh.</li>
            <li>If any provision of these terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</li>
          </ul>
        </Section>

        {/* 12 */}
        <Section title="Changes to Terms">
          <p>
            We reserve the right to update or replace these Terms of Service at any time. Changes
            will be posted on this page with an updated date. It is your responsibility to check
            these terms periodically for changes.
          </p>
          <p className="mt-3">
            Your continued use of our website or services after any changes are posted constitutes
            your acceptance of the new terms. If you do not agree with the updated terms, you must
            stop using our website immediately.
          </p>
        </Section>

        {/* 13 */}
        <Section title="Contact Us">
          <p>
            If you have any questions about these Terms of Service, please contact us. We aim to
            respond to all enquiries within 1–2 business days.
          </p>
          <ul className="list-none pl-0 space-y-1 mt-3">
            <li><strong>Email:</strong> hello@aishcraft.com</li>
            <li><strong>Address:</strong> Aishcraft, Bhopal, Madhya Pradesh, India</li>
            <li><strong>Response Time:</strong> 1–2 business days</li>
          </ul>
        </Section>

      </div>
    </div>
  );
};

export default TermsOfService;